import type { RefObject } from "react";
import React from "react";

import { useTranslation } from "@litres-libs/i18n";
import { useAppSelector } from "@litres-core/store";
import { useEventListener } from "@litres-libs/global-hooks";
import { ContextMenuItem } from "@litres/ui-kit/context-menu";

import { trackerClick } from "../utils";
import type { LowerMenuItemType } from "../types";
import { isFocusElement } from "../../../utils/domHelpers";
import { KeyCodes } from "../../SuggestionsSearch/constants";
import { useRefCallback } from "../../../hooks/useRefCallback";

interface IMoreItemsProps {
  items: LowerMenuItemType[];
  moreButtonRef?: RefObject<HTMLInputElement>;
}

export const MoreItems = ({ items, moreButtonRef }: IMoreItemsProps) => {
  const userId = useAppSelector(state => state.foundationUser.id);
  const { t } = useTranslation();
  const isUserAnonymous = userId === 0;
  const [moreMenuListNode, moreMenuRefCallback] =
    useRefCallback<HTMLDivElement>();

  const handleClick = (title: string, id: string) => {
    trackerClick(title, id, isUserAnonymous);
  };

  const handleKeyPress = (event: Event): void => {
    if (!moreMenuListNode) {
      return;
    }

    const { key } = event as KeyboardEvent;

    const menuItems = moreMenuListNode
      ? Array.from(moreMenuListNode.children)
      : [];

    const firstIndex = 0;
    const lastIndex = menuItems.length - 1;
    const currentIndex = menuItems.findIndex(isFocusElement);

    const prevIndex = isFocusElement(menuItems[firstIndex])
      ? lastIndex
      : currentIndex - 1;

    const nextIndex = isFocusElement(menuItems[lastIndex])
      ? firstIndex
      : currentIndex + 1;

    if (key === KeyCodes.ArrowDown) {
      (
        menuItems[currentIndex === -1 ? firstIndex : nextIndex] as HTMLElement
      ).focus();
    }

    if (key === KeyCodes.ArrowUp) {
      if (
        moreButtonRef?.current &&
        (currentIndex === -1 || prevIndex === lastIndex)
      ) {
        moreButtonRef.current.focus();
      } else {
        (
          menuItems[currentIndex === -1 ? lastIndex : prevIndex] as HTMLElement
        ).focus();
      }
    }
  };

  useEventListener("keydown", handleKeyPress);

  return (
    <div data-testid="lowerMenu_moreItems" ref={moreMenuRefCallback}>
      {items.map(({ title, href, id }) => (
        <ContextMenuItem
          key={id}
          onClick={handleClick.bind(null, title, id)}
          asLink
          href={href}
          text={t(title)}
          data-testid="lowerMenu_moreItem"
        />
      ))}
    </div>
  );
};
